export type ArithmeticOperator = 'ADD' | 'SUB' | 'MUL' | 'DIV';

/**
 * The shape the shared completeness rule reads off a term.
 *
 * Deliberately structural, and deliberately all-optional past `type`: the two
 * cubes model a term differently — the Klinik-Cube as a closed discriminated
 * union of plain objects, the Virtual-Clinic analysis as a class hierarchy
 * behind an abstract base — and neither has to change shape to be read here. A
 * class instance satisfies a structural interface, so nothing is converted,
 * wrapped or re-typed at the call site.
 *
 * `TOperand` names what a function's operands are, so a caller keeps its own
 * term type all the way into the recursion hook below instead of casting back
 * out of a widened one.
 */
export interface CalculationTermLike<TOperand = unknown> {
  readonly type: string;
  readonly value?: number | null;
  readonly operatingNumberId?: string | null;
  readonly operation?: string | null;
  readonly leftOperand?: TOperand | null;
  readonly rightOperand?: TOperand | null;
}

/**
 * Whether a term is finished enough to send.
 *
 * A half-built formula is the normal state of a term being edited, so both
 * cubes' forms need to tell "still being written" apart from "ready" — and
 * both had spelled the same three-case rule out by hand:
 *
 * - a constant needs a FINITE number. The backend rejects a non-finite one at
 *   the transport level, before any engine sees it, so catching it here turns
 *   a 400 into a disabled button.
 * - a metric leaf needs a non-empty id. Everything that is neither a constant
 *   nor a function is one, whatever its discriminator reads: the Klinik-Cube
 *   calls it `clinicOperatingNumber`, the Virtual-Clinic analysis has two
 *   names for it, and the rule does not care which.
 * - a function needs an operator and two present, finished operands.
 *
 * `isOperandComplete` is how the recursion is spelled, defaulting to this
 * function. A model whose leaves carry rules of their own — the
 * fully-inpatient-case leaf additionally requires a valid case filter — passes
 * its own polymorphic check instead, so sharing the structure never quietly
 * drops what one of the models demands of a leaf.
 */
export function isCalculationTermComplete<
  TTerm extends CalculationTermLike<TTerm>,
>(
  term: TTerm | null | undefined,
  isOperandComplete: (operand: TTerm) => boolean = (operand) =>
    isCalculationTermComplete(operand),
): boolean {
  if (!term) {
    return false;
  }

  switch (term.type) {
    case 'constant':
      return Number.isFinite(term.value);
    case 'function':
      return (
        !!term.operation &&
        !!term.leftOperand &&
        isOperandComplete(term.leftOperand) &&
        !!term.rightOperand &&
        isOperandComplete(term.rightOperand)
      );
    default:
      return !!term.operatingNumberId;
  }
}
